import React from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ApiService } from './api';

// URLs use the event's slug (/register/up-pathcon-2026); API calls keep using the id.
const STORAGE_KEY = 'eventSlugs';
const OBJECT_ID = /^[0-9a-f]{24}$/;

const load = () => {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}') || {};
  } catch (_) {
    return {};
  }
};

let slugsById = load();

const persist = () => {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(slugsById)); } catch (_) { /* storage unavailable */ }
};

export const isEventId = (value) => OBJECT_ID.test(value || '');

/** Records id -> slug from any event payload so later links can use the slug. */
export const rememberEvents = (events) => {
  let changed = false;
  (Array.isArray(events) ? events : [events]).forEach((event) => {
    if (event?.id && event.slug && slugsById[event.id] !== event.slug) {
      slugsById[event.id] = event.slug;
      changed = true;
    }
  });
  if (changed) persist();
};

/** What to put in a URL for an event id (its slug when known). */
export const eventRef = (idOrEvent) => {
  if (!idOrEvent) return idOrEvent;
  if (typeof idOrEvent === 'object') return idOrEvent.slug || idOrEvent.id;
  return slugsById[idOrEvent] || idOrEvent;
};

const idForSlug = (slug) => Object.keys(slugsById).find((id) => slugsById[id] === slug);

/** Resolved event id plus the reference as it appears in the URL. */
export const useEventRoute = () => {
  const { eventId: ref } = useParams();
  const known = isEventId(ref) ? ref : idForSlug(ref);
  const { data, isError } = useQuery({
    queryKey: ['eventRef', ref],
    queryFn: async () => {
      const resolved = await ApiService.resolveEvent(ref);
      rememberEvents({ id: resolved.id, slug: resolved.slug });
      return resolved;
    },
    enabled: !!ref && !known,
    staleTime: Infinity,
    retry: 1,
  });
  const eventId = known || data?.id;
  return { eventId, eventRef: ref, resolving: !!ref && !eventId && !isError, notFound: !!ref && !eventId && isError };
};

/** Waits for a slug in the URL to resolve so pages never see an undefined event id. */
export const EventRefGate = ({ children }) => {
  const { resolving, notFound } = useEventRoute();
  if (resolving) {
    return (
      <div className="min-h-[40vh] flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" />
      </div>
    );
  }
  if (notFound) {
    return <div className="p-8 text-center text-gray-600">This event link is not valid.</div>;
  }
  return children;
};
